import { useState, useCallback, useEffect } from "react";
import { Lock, Train, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { apiClient } from "@/App";
import { logger } from "@/lib/logger";
import PnjLogo from "@/components/PnjLogo";

export default function PasscodeGate({ onSuccess }) {
  const [passcode, setPasscode] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = useCallback(async () => {
    if (passcode.length !== 6) {
      toast.error("Masukkan 6 digit passcode");
      return;
    }
    setLoading(true);
    try {
      await apiClient.post("/auth/verify", { passcode });
      toast.success("Akses diberikan");
      onSuccess(passcode);
    } catch (err) {
      logger.debug("passcode verify failed", err);
      toast.error("Passcode salah");
      setPasscode("");
    } finally {
      setLoading(false);
    }
  }, [passcode, onSuccess]);

  useEffect(() => {
    if (passcode.length === 6) submit();
  }, [passcode, submit]);

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-[#0c1b2a] text-white">
      {/* Backdrop layers */}
      <div className="absolute inset-0 bg-grid opacity-40" />
      <div
        className="absolute inset-0 opacity-20 mix-blend-screen"
        style={{
          backgroundImage:
            "url(https://images.pexels.com/photos/19999803/pexels-photo-19999803.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "blur(18px) saturate(0.7)",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0c1b2a]/40 via-[#0c1b2a]/85 to-[#0c1b2a]" />

      <div className="relative z-10 flex min-h-screen">
        {/* Left rail */}
        <aside className="hidden lg:flex w-[42%] flex-col justify-between p-14 border-r border-[#243a52]">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-md bg-[#034694] flex items-center justify-center">
              <Train className="h-6 w-6" strokeWidth={2.4} />
            </div>
            <div>
              <div className="text-[11px] tracking-[0.32em] text-slate-300 uppercase">
                Shunting Alerts
              </div>
              <div className="text-sm font-display font-semibold">
                Depo Dispatcher Console
              </div>
            </div>
          </div>

          <div>
            <div className="text-xs tracking-[0.32em] text-[#289643] uppercase mb-4">
              Shunting Alerts · Startup & Departure
            </div>
            <h1 className="font-display text-5xl xl:text-6xl font-800 leading-[1.02]">
              Startup &<br />
              Departure<br />
              <span className="text-[#289643]">Alert System.</span>
            </h1>
            <p className="mt-6 text-slate-300 max-w-md leading-relaxed">
              Pengingat suara & notifikasi untuk setiap gerakan trainset — T-30
              menit sebelum keberangkatan hingga jam keberangkatan sesuai
              shunting plan.
            </p>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <ShieldCheck className="h-4 w-4 text-[#289643]" />
              Akses terkunci passcode · v1.0
            </div>
            <div className="pt-3 border-t border-[#243a52]" data-testid="pnj-credit-gate">
              <PnjLogo size={44} />
            </div>
          </div>
        </aside>

        {/* Right panel — passcode terminal */}
        <main className="flex-1 flex items-center justify-center p-8">
          <div
            className="w-full max-w-md border border-[#243a52] bg-[#14283d]/80 backdrop-blur-xl rounded-2xl p-10 shadow-[0_0_0_1px_rgba(2,70,148,0.15)]"
            data-testid="passcode-panel"
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="h-10 w-10 rounded-md border border-[#243a52] bg-[#0c1b2a] flex items-center justify-center">
                <Lock className="h-5 w-5 text-[#FFFF00]" strokeWidth={2.2} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400">
                  Secure Terminal
                </div>
                <div className="text-lg font-display font-semibold">
                  Enter Passcode
                </div>
              </div>
            </div>

            <div className="mb-2 text-xs text-slate-400 uppercase tracking-widest">
              6-digit access code
            </div>
            <InputOTP
              maxLength={6}
              value={passcode}
              onChange={setPasscode}
              data-testid="passcode-input"
              containerClassName="justify-start gap-2"
            >
              <InputOTPGroup>
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <InputOTPSlot
                    key={i}
                    index={i}
                    className="h-14 w-12 text-2xl font-mono border-[#243a52] bg-[#0c1b2a] text-white data-[active=true]:ring-2 data-[active=true]:ring-[#289643] data-[active=true]:border-[#289643]"
                  />
                ))}
              </InputOTPGroup>
            </InputOTP>

            <Button
              onClick={submit}
              disabled={loading}
              data-testid="passcode-submit"
              className="mt-8 w-full h-12 rounded-full bg-[#289643] hover:bg-[#1f7a35] text-white font-display font-semibold tracking-wide transition-colors duration-150"
            >
              {loading ? "Memverifikasi…" : "Masuk ke Dispatcher"}
            </Button>

            <div
              className="mt-8 pt-6 border-t border-[#243a52] flex items-center justify-between gap-3 lg:hidden"
              data-testid="pnj-credit-gate-mobile"
            >
              <PnjLogo size={36} />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
